<template>
	<fieldset id="fs-temperature" class="pt-3 pb-3">
		<div class="container pt-5 pb-3">
			<div class="row no-gutters">
				<div class="fieldset-title">
					<UseIcon asset="sprite.svg#icon-thermostat" />

					<h2 class="heading">Water Temperature</h2>
				</div>
			</div>

			<div class="row no-gutters pb-4 pb-xxl-0">
				<div class="col-12 col-xl-7 mb-5 mb-xl-0">
					<div class="row">
						<div class="col-3">
							<FormInput
								dataKey="incomingT"
								inputClass="field-unit"
								:disabled="sizerStore.userToggledERSizing"
							>
								<template v-slot:icon>
									<UseIcon asset="sprite.svg#symbol-fahrenheit" />
								</template>

								<template v-slot:tooltip-content>
									<p>This is the coldest water temperature supplied by incoming city water.</p>

									<p>Increasing this value means the HPWH has a lower temperature lift to the hot water supply temperature, and the required heating capacity will decrease.</p>
								</template>
							</FormInput>
						</div>

						<div class="col-3 offset-1">
							<FormInput
								dataKey="supplyT"
								inputClass="field-unit"
								:readOnly="formData.CAspec"
							>
								<template v-slot:icon>
									<UseIcon asset="sprite.svg#symbol-fahrenheit" />
								</template>

								<template v-slot:tooltip-content>
									<p>This is the water temperature supplied to the building occupants from the hot water system/plant. The default is set to 120F, an industry standard. If you are aligning sizing efforts with CA Title 24 software this should be increased to 125F.</p>

									<p>Increasing the supply water temperature raises the reference temperature for what the occupants use. This will increase the storage volume as the hot water stored has less potential energy over the supply water temperature.</p>
								</template>

								<template v-slot:locked-content>
									<i class="symbol help left">
										<object tabindex="-1" type="image/svg+xml" data="/static/	build/gfx/svg/symbol-lock.svg"></object>
									</i>Locked to CBECC-Res
								</template>
							</FormInput>
						</div>

						<div class="col-3 offset-1">
							<FormInput
								dataKey="storageT"
								inputClass="field-unit"
								:disabled="sizerStore.userToggledERSizing"
							>
								<template v-slot:icon>
									<UseIcon asset="sprite.svg#symbol-fahrenheit" />
								</template>

								<template v-slot:tooltip-content>
									<p>The hot water storage temperature.</p>

									<p>The temperature setpoint of water stored in the storage tank(s)</p>
								</template>
							</FormInput>
						</div>
					</div>
				</div>

				<div class="col-8 offset-4 col-xl-5 offset-xl-0">
					<div
						:class="{
							'advanced-options': true,
							'p-0': true,
							expanded: showAdvancedOpts,
						}"
					>
						<button
							:class="{
								'btn btn-box': true,
								'js-adv-options': true,
								toggled: showAdvancedOpts,
							}"
							:aria-expanded="showAdvancedOpts"
							@click="userToggledAdvancedOpts = !showAdvancedOpts"
							type="button"
						>
							<span>Advanced Options</span>

							<UseIcon iconClass="symbol" asset="sprite.svg#icon-gears" />

							<i class="symbol secondary close xl"></i>
						</button>

						<div class="row no-gutters adv-option-groups p-xl-2">
							<div class="fieldgroup row no-gutters p-5">
								<div class="col-5">
									<div :class="['field-wrap', { 'warning': formData.aquastatFraction < 100 - formData.percentUseable }]">
										<label>
											<div class="flex items-center gap-1">
												<span style="white-space: nowrap;">Aquastat Fraction</span>
												<Tooltip title="Aquastat Fraction" align="left">
													<p>The percentage of tank height (from bottom of the storage tank) at which the ON temperature sensor will be located. When the water at this location drops to the ON temperature setpoint, the tanks will begin heating.</p>
												</Tooltip>
											</div>
										</label>

										<div class="row no-gutters field-unit">
											<input
												type="number"
												:value="formData.aquastatFraction"
												@input="e => formData.aquastatFraction = e.target.value"
												required
												step="any"
																		>

											<UseIcon asset="sprite.svg#symbol-percent" />
										</div>

										<div class="suffix-wrap">
											<p class="desc"></p>
											<p class="label-warning" v-if="formData.aquastatFraction < 100 - formData.percentUseable">
												<WarningIcon />
												Warning: The aquastat fraction is in the unusable portion of the tank based on the input for drawdown
											</p>
										</div>
									</div>
								</div>

								<div class="col-5 offset-1">
									<div class="field-wrap">
										<label>
											<div class="flex items-center gap-1">
												Drawdown
												<Tooltip title="Drawdown">
													<p>Percentage of tank that is usable. This should be equivalent to 100 minus the percentage height from the bottom of the tank where the hot water inlet (hot water returning to the tank from the HPWH) is located.</p>
												</Tooltip>
											</div>
										</label>

										<div class="row no-gutters field-unit">
											<input
												type="number"
												:value="formData.percentUseable"
												@input="e => formData.percentUseable = e.target.value"
												required
												step="any"
											>

											<UseIcon asset="sprite.svg#symbol-percent" />
										</div>

										<div class="suffix-wrap">
											<p class="desc"></p>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</fieldset>
</template>

<script>
import UseIcon from '@/components/UseIcon'
import FormInput from '@/components/FormInput'
import WarningIcon from '@/components/WarningIcon'
import Tooltip from '@/components/Tooltip'
import formData from '@/components/Sizer/form-data-store'
import sizerStore from '@/components/Sizer/sizer-store'

export default {
	name: 'WaterTempInputs',

	components: {
		UseIcon,
		FormInput,
		WarningIcon,
		Tooltip,
	},

	data: () => ({
		formData,
		sizerStore,
		userToggledAdvancedOpts: undefined,
	}),

	computed: {
		showAdvancedOpts () {
			const { userToggledAdvancedOpts } = this

			return typeof userToggledAdvancedOpts === 'boolean' ?
				userToggledAdvancedOpts :
				formData.errors
		},

	},

	watch: {
		'formData.supplyT': {
			handler(newSupplyT) {
				if (newSupplyT) {
					this.formData.recircTemp = Number(newSupplyT) - 5
				}
			}
		},
		'formData.percentUseableError' (newVal) {
			// Force advanced options open when there's a percentUseable/Drawdown error
			if (newVal === true) {
				this.userToggledAdvancedOpts = true
			}
		}
	},

	methods: {
		handleStorageTInput ({ target: { value } }) {
			console.log("hi hello!")
			formData.storageT = value
			formData.lsShed_TempSetHPWHOut = value
			formData.lsNorm_TempSetOffTrig = value - 10
			formData.lsLoadUp_TempSetOffTrig = value - 10
			formData.lsShed_TempSetOffTrig = value - 10
			formData.lsLoadUp_TempSetHPWHOut = Number(value) + 10
		}
	}
}
</script>

<style scoped>
/* Allow tooltips to overflow the advanced options container when expanded */
.advanced-options.expanded :deep(.adv-option-groups),
.advanced-options.expanded .adv-option-groups {
	overflow: visible;
}

</style>
